import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { forkJoin } from 'rxjs';
import { LearningResourceEnrollment } from 'src/app/models/learning_resource_enrollment.model';
import { User } from 'src/app/models/user.model';
import { WorkerFormationOverview } from 'src/app/models/worker_formation_overview.model';
import { ApiService, PermissionsService } from 'src/app/services';

@Component({
  selector: 'app-formations-by-user-tab',
  templateUrl: './formations-by-user-tab.component.html',
  styleUrl: './formations-by-user-tab.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButtonModule, MatIconModule, MatTableModule, MatTooltipModule, RouterLink],
})
export class FormationsByUserTabComponent implements OnInit {
  private readonly api_service = inject(ApiService);
  private readonly change_detector_ref = inject(ChangeDetectorRef);
  private readonly destroy_ref = inject(DestroyRef);
  private readonly permissions_service = inject(PermissionsService);

  readonly loading_overview = signal(false);

  overview_data_source = new MatTableDataSource<WorkerFormationOverview>([]);

  overview_displayed_columns: string[] = ['user', 'active_formation', 'hours', 'actions'];

  private worker_role_id: number | null = null;

  ngOnInit(): void {
    this.listenRoles();
  }

  private listenRoles(): void {
    this.permissions_service.rolesObservable.pipe(takeUntilDestroyed(this.destroy_ref)).subscribe(roles => {
      if (roles == null) {
        return;
      }

      const worker_role = Object.values(roles).find(role => role.slug === 'worker');
      this.worker_role_id = worker_role?.id ?? null;
      this.loadWorkersOverview();
    });
  }

  private loadWorkersOverview(): void {
    if (this.worker_role_id == null) {
      return;
    }

    this.loading_overview.set(true);

    forkJoin({
      workers: this.api_service.getUsers({
        role_id: this.worker_role_id,
        scopes: 'activeWorkers',
        order_by: 'name:asc',
        per_page: 500,
        with: 'role',
      }),
      active_enrollments: this.api_service.getLearningResourceEnrollments({
        scopes: 'active',
        per_page: 500,
        with: 'learning_resource,user',
        append: 'time_registered_in_seconds',
      }),
    })
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: ({ workers, active_enrollments }): void => {
          const active_enrollments_by_user_id = active_enrollments.data.reduce((grouped_enrollments, enrollment) => {
            const user_enrollments = grouped_enrollments.get(enrollment.user_id) ?? [];
            user_enrollments.push(enrollment);
            grouped_enrollments.set(enrollment.user_id, user_enrollments);

            return grouped_enrollments;
          }, new Map<number, LearningResourceEnrollment[]>());

          this.overview_data_source.data = workers.data.map(worker =>
            this.serializeWorkerFormationOverview(worker, active_enrollments_by_user_id.get(worker.id) ?? [])
          );
          this.loading_overview.set(false);
          this.change_detector_ref.markForCheck();
        },
        error: (): void => {
          this.loading_overview.set(false);
          this.change_detector_ref.markForCheck();
        },
      });
  }

  private serializeWorkerFormationOverview(user: User, active_enrollments: LearningResourceEnrollment[]): WorkerFormationOverview {
    const has_active_enrollment = active_enrollments.length > 0;
    const total_time_registered_in_seconds = active_enrollments.reduce(
      (total_seconds, enrollment) => total_seconds + (enrollment.time_registered_in_seconds ?? 0),
      0
    );

    return {
      user,
      user_full_name: `${user.name} ${user.surnames}`.trim(),
      active_formation_name: has_active_enrollment
        ? active_enrollments.map(enrollment => enrollment.learning_resource?.name ?? '—').join(', ')
        : '—',
      formation_hours_label: has_active_enrollment ? this.formatSecondsAsHours(total_time_registered_in_seconds) : '—',
    };
  }

  private formatSecondsAsHours(seconds: number): string {
    const hours = seconds / 3600;
    return `${hours.toFixed(1)} h`;
  }
}
